import {
  ProjectChangeType,
  nonVersionChangeTypes,
  type ProjectChange,
} from '../pipeline/update-engine.models';
import { getProposals } from '../proposals/proposals.helpers';
import { isOneOf } from '../array.helpers';
import type { Baseline, Project } from '../../models/project.model';
import type { ProjectEventActivity } from '../../models/project-event.model';

const findVersion = (
  project: Project,
  versionId: string,
): Baseline | undefined => project.baselines.find(({ id }) => id === versionId);

const proposalsOf = (project: Project, versionId: string) => {
  const version = findVersion(project, versionId);
  return version ? getProposals(version) : [];
};

const actionIdsForProposal = (
  project: Project,
  proposalId: string,
): string[] => {
  for (const version of project.baselines) {
    const proposal = getProposals(version).find(({ id }) => id === proposalId);
    if (proposal) return proposal.actionIds;
  }
  return [];
};

/** Entries one proposal or version change stands for. */
const changeEvents = (
  change: ProjectChange,
  before: Project,
  after: Project,
): ProjectEventActivity[] => {
  switch (change.type) {
    case ProjectChangeType.ProposalAdd:
    case ProjectChangeType.ProposalDuplicate: {
      const existing = new Set(
        proposalsOf(before, change.versionId).map(({ id }) => id),
      );
      return proposalsOf(after, change.versionId)
        .filter(({ id }) => !existing.has(id))
        .map(({ id }) => ({ kind: 'proposal-created', proposalId: id }));
    }
    case ProjectChangeType.ProposalDelete: {
      const proposal = proposalsOf(before, change.versionId).find(
        ({ id }) => id === change.proposalId,
      );
      return proposal
        ? [
            {
              kind: 'proposal-deleted',
              proposalId: proposal.id,
              name: proposal.name,
            },
          ]
        : [];
    }
    case ProjectChangeType.VersionAdd:
      return findVersion(after, change.versionId)
        ? [{ kind: 'version-created', versionId: change.versionId }]
        : [];
    case ProjectChangeType.VersionRemove: {
      const version = findVersion(before, change.versionId);
      return version
        ? [
            {
              kind: 'version-deleted',
              versionId: version.id,
              name: version.name,
            },
          ]
        : [];
    }
    default:
      return [];
  }
};

/**
 * History entries for one apply, read from its {@link ProjectChange}s:
 * proposals and versions added or removed, a baseline edit when element
 * changes land on the baseline, and each action added to the selected
 * proposal. Callers pass anything the changes cannot say themselves.
 */
export const appliedChangesEvents = ({
  before,
  after,
  changes,
  versionId,
  proposalId,
}: {
  before: Project;
  after: Project;
  changes: readonly ProjectChange[];
  versionId: string | undefined;
  proposalId: string | undefined;
}): ProjectEventActivity[] => {
  const events = changes.flatMap((change) =>
    changeEvents(change, before, after),
  );
  if (!proposalId) {
    const editsBaseline = changes.some(({ type }) =>
      isOneOf(nonVersionChangeTypes, type),
    );
    if (editsBaseline && versionId) {
      events.push({ kind: 'baseline-edited', versionId });
    }
    return events;
  }
  const previous = new Set(actionIdsForProposal(before, proposalId));
  for (const actionId of actionIdsForProposal(after, proposalId)) {
    if (!previous.has(actionId))
      events.push({ kind: 'action-created', actionId });
  }
  return events;
};
